import React, { useContext, useEffect } from 'react'
import { useCurrentUser } from '../hooks/useUserContext'
import { ThemeProvider, StyledEngineProvider, useColorScheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { theme } from '../../styles/theme/theme'
import { createTheme } from '@mui/material/styles'
import pwsLogo from '../../img/logo_powerspace.svg'
import { ThemeTransition } from '../../components/layout/ThemeTransition'

const MODE_STORAGE_KEY = 'mui-mode'

const InitializeThemeMode: React.FC = () => {
  const { setMode } = useColorScheme()

  useEffect(() => {
    if (localStorage.getItem(MODE_STORAGE_KEY) === null) {
      setMode('system')
    }
  }, [setMode])

  return null
}

const LogoContext = React.createContext<string>(pwsLogo)
LogoContext.displayName = 'LogoContext'

type LogoContextProviderProps = {
  logo: string
  children: React.ReactNode
}

const LogoContextProvider: React.FC<LogoContextProviderProps> = ({ children, logo }): JSX.Element => {
  return <LogoContext.Provider value={logo}>{children}</LogoContext.Provider>
}

export function useLogo(): string {
  const value = useContext(LogoContext)
  if (!value) throw new Error('provider was not used')
  return value as string
}

const companySecondaryPalette = (primaryColor: string, secondaryColor: string) => ({
  secondary: {
    main: primaryColor,
    dark: secondaryColor,
  },
})

export const ThemeContext: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    me: { company: currentUserCompany },
  } = useCurrentUser()

  const userTheme = React.useMemo(() => {
    if (currentUserCompany.style) {
      const secondary = companySecondaryPalette(
        currentUserCompany.style.primaryColor,
        currentUserCompany.style.secondaryColor
      )
      return createTheme(theme, {
        colorSchemes: {
          light: { palette: secondary },
          dark: { palette: secondary },
        },
      })
    }
    return theme
  }, [currentUserCompany])

  const logoToDisplay = currentUserCompany.style?.logoUrl || pwsLogo

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={userTheme} defaultMode="system" noSsr disableTransitionOnChange={false}>
        <CssBaseline />
        <InitializeThemeMode />
        <ThemeTransition />
        <LogoContextProvider logo={logoToDisplay}>{children}</LogoContextProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}
